/**
 * @file src/config/queryKeys.js
 * @description Centralised React Query cache key factory.
 * Rules:
 *  - No functions beyond key factories — no React, no axios
 *  - Every useQuery / useMutation / queryClient.invalidateQueries call
 *    must reference these keys — never hardcode strings inline
 *  - Array format used throughout for granular cache invalidation
 *  - Factory functions (e.g. byId) allow targeted invalidation
 *    without blowing the entire cache
 *
 * Usage:
 *  useQuery({ queryKey: QUERY_KEYS.invoices.list() })
 *  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all })
 *  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.byId(id) })
 */

export const QUERY_KEYS = {
  // ── Auth / User ────────────────────────────────────────────────────────
  auth: {
    /** Matches ALL auth queries — use for full auth cache wipe on logout */
    all: ["auth"],
    /** Authenticated user profile — /auth/me */
    me: () => ["auth", "me"],
  },

  // ── Legacy company settings ────────────────────────────────────────────
  company: {
    /** Matches selected company settings queries — /auth/get-company */
    all: ["company"],
    /** Single selected company record — /auth/get-company */
    detail: () => ["company", "detail"],
  },

  // ── Multi-company context ──────────────────────────────────────────────
  companies: {
    /** Matches ALL multi-company context queries */
    all: ["companies"],
    /** Companies current user belongs to — /companies/mine */
    mine: () => ["companies", "mine"],
    /** Active selected company — /companies/active */
    active: () => ["companies", "active"],
  },

  // ── Branches ───────────────────────────────────────────────────────────
  branches: {
    all: ["branches"],
    list: (params = {}) => ["branches", "list", params],
    detail: (id) => ["branches", "detail", id],
  },

  // ── Invitations ────────────────────────────────────────────────────────
  memberships: {
    all: ["memberships"],
    list: (params = {}) => ["memberships", "list", params],
  },
  invitations: {
    all: ["invitations"],
    list: (params = {}) => ["invitations", "list", params],
    lookup: (token) => ["invitations", "lookup", token],
  },

  // ── Invoices ───────────────────────────────────────────────────────────
  invoices: {
    /** Matches ALL invoice queries */
    all: ["invoices"],
    /** Paginated invoice list — /invoices/list */
    list: () => ["invoices", "list"],
    /** Single invoice by ID — /invoices/:id */
    byId: (id) => ["invoices", "detail", id],
    /** Invoice by file name — /invoices/by-name/:name */
    byName: (name) => ["invoices", "byName", name],
    /** Invoice audit trail — /invoices/:id/audit-trail */
    auditTrail: (id) => ["invoices", "auditTrail", id],
    openByClient: (clientId, params = {}) => [
      "invoices",
      "openByClient",
      clientId,
      params,
    ],
    /**
     * Extracted invoice data from upload processing.
     * Persisted to localStorage via InvoiceAutoSave in
     * invoice-processing-queue page.
     */
    extracted: () => ["invoices", "extracted"],
  },

  // ── Invoice uploads / batch ────────────────────────────────────────────
  batches: {
    /** Matches ALL batch queries */
    all: ["batches"],
    /** Batch status list — /invoice-uploads/batch/status */
    status: () => ["batches", "status"],
    /** Invoice processing progress — /invoice-uploads/get-progress */
    progress: () => ["batches", "progress"],
  },

  // ── Clients ────────────────────────────────────────────────────────────
  clients: {
    /** Matches ALL client queries */
    all: ["clients"],
    /** Full client list — /clients/ */
    list: () => ["clients", "list"],
    /** Search results — /clients/?search=query */
    search: (query) => ["clients", "search", query],
    ledger: (clientId, params = {}) => ["clients", "ledger", clientId, params],
    balance: (clientId, params = {}) => [
      "clients",
      "balance",
      clientId,
      params,
    ],
  },

  // ── Vendors ────────────────────────────────────────────────────────────
  vendors: {
    /** Matches ALL vendor queries */
    all: ["vendors"],
    /** Full vendor list — /vendors/ */
    list: () => ["vendors", "list"],
  },

  // ── Products ───────────────────────────────────────────────────────────
  products: {
    /** Matches ALL product queries */
    all: ["products"],
    /** Full product list */
    list: () => ["products", "list"],
  },

  // ── Devices ────────────────────────────────────────────────────────────
  devices: {
    all: ["devices"],
    list: (params = {}) => ["devices", "list", params],
  },

  // ── Audit logs ─────────────────────────────────────────────────────────
  auditLogs: {
    /** Matches ALL audit log queries */
    all: ["auditLogs"],
    /** Paginated audit logs for selected company — /auth/audit-logs */
    list: (page, limit) => ["auditLogs", "list", page, limit],
  },

  // ── Payments ───────────────────────────────────────────────────────────
  payments: {
    all: ["payments"],
    list: () => ["payments", "list"],
  },

  // ── Refunds & Credit Notes ─────────────────────────────────────────────
  refunds: {
    all: ["refunds"],
    list: () => ["refunds", "list"],
  },
  clientCreditApplications: {
    all: ["clientCreditApplications"],
    list: () => ["clientCreditApplications", "list"],
  },
  creditNotes: {
    all: ["creditNotes"],
    list: () => ["creditNotes", "list"],
  },

  // ── Purchase Orders ────────────────────────────────────────────────────
  purchaseOrders: {
    all: ["purchaseOrders"],
    list: () => ["purchaseOrders", "list"],
  },

  // ── Purchase sub-modules ───────────────────────────────────────────────
  purchasePayments: {
    all: ["purchasePayments"],
    list: () => ["purchasePayments", "list"],
  },

  purchaseRefunds: {
    all: ["purchaseRefunds"],
    list: () => ["purchaseRefunds", "list"],
  },

  purchaseCreditNotes: {
    all: ["purchaseCreditNotes"],
    list: () => ["purchaseCreditNotes", "list"],
  },

  expenses: {
    all: ["expenses"],
    list: () => ["expenses", "list"],
  },

  accounting: {
    all: ["accounting"],
    accounts: (params = {}) => ["accounting", "accounts", params],
    trialBalance: (params = {}) => ["accounting", "trial-balance", params],
    generalLedger: (params = {}) => ["accounting", "general-ledger", params],
    arAging: (params = {}) => ["accounting", "ar-aging", params],
    profitAndLoss: (params = {}) => ["accounting", "profit-loss", params],
    balanceSheet: (params = {}) => ["accounting", "balance-sheet", params],
    journalEntries: (params = {}) => ["accounting", "journal-entries", params],
    journalEntry: (id) => ["accounting", "journal-entry", id],
  },
};
